import { API_BASE_URL } from './api.js';
import { message } from 'antd';

let onUnauthorized = null;
export const setUnauthorizedCallback = (callback) => { onUnauthorized = callback; };

// --- Queue para evitar múltiples refresh simultáneos ---
let isRefreshing = false;
let pendingQueue = [];
const processQueue = (error, newToken = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
  pendingQueue = [];
};

export const apiClient = {
  getToken: () => {
    const user = localStorage.getItem('user');
    if (!user) return null;
    try { return JSON.parse(user).token; } catch { return null; }
  },

  // ---------------------------------------------------------------------------
  // Request con retry automático y refresh token
  // ---------------------------------------------------------------------------
  async request(url, options = {}, _retry = false) {
    const token = apiClient.getToken();
    const headers = { ...options.headers };

    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) headers['Authorization'] = `Bearer ${token}`;

    const config = { ...options, headers };
    const response = await fetch(url, config);

    if (response.status !== 401) return response;

    // --- Detectar token vencido ---
    let payload = null;
    try { payload = await response.clone().json(); } catch {}
    const expired =
      payload?.message === 'Token no válido' ||
      payload?.error?.name === 'TokenExpiredError' ||
      payload?.message?.toLowerCase?.().includes('expired') ||
      payload?.message?.toLowerCase?.().includes('vencido');

    if (_retry || !expired) {
      if (typeof onUnauthorized === 'function') onUnauthorized();
      return response;
    }

    // -------------------------------------------------------------------------
    // REFRESH FLOW
    // -------------------------------------------------------------------------
    try {
      if (isRefreshing) {
        const newToken = await new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        });
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        return fetch(url, { ...config, headers: retryHeaders });
      }

      isRefreshing = true;
      let r;

      // Web → preferir cookie, pero usar refreshToken de localStorage si existe
      const localRT = localStorage.getItem('refreshToken');
      if (localRT) {
        r = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: localRT }),
        });
      } else {
        r = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // cookie httpOnly
        });
      }

      if (!r.ok) {
        isRefreshing = false;
        processQueue(new Error('Refresh failed'));
        if (typeof onUnauthorized === 'function') onUnauthorized();
        return response;
      }

      const { token: newToken, usuario, refreshToken: newRefreshFromBody } = await r.json();

      // --- Guardar token actualizado en user ---
      try {
        const raw = localStorage.getItem('user');
        const prev = raw ? JSON.parse(raw) : {};
        localStorage.setItem('user', JSON.stringify({ ...prev, ...usuario, token: newToken }));
      } catch {}

      // --- Si vino un refresh nuevo, actualizar en localStorage ---
      if (newRefreshFromBody) {
        localStorage.setItem('refreshToken', newRefreshFromBody);
      }

      isRefreshing = false;
      processQueue(null, newToken);

      // --- Reintentar original con el nuevo access token ---
      const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
      return fetch(url, { ...config, headers: retryHeaders });
    } catch (e) {
      isRefreshing = false;
      processQueue(e, null);
      if (typeof onUnauthorized === 'function') onUnauthorized();
      return response;
    }
  },

  // ---------------------------------------------------------------------------
  // Métodos HTTP helpers
  // ---------------------------------------------------------------------------
  get: (endpoint, options = {}) => apiClient.request(`${API_BASE_URL}${endpoint}`, options),

  post: (endpoint, data, options = {}) => {
    const isFormData = data instanceof FormData;
    const headers = { ...(options.headers || {}) };

    if (isFormData) {
      return apiClient.request(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        body: data,
        headers,
        ...options,
      });
    }
    return apiClient.request(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers,
      ...options,
    });
  },

  put: (endpoint, data, options = {}) => {
    const headers = { ...(options.headers || {}) };
    return apiClient.request(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers,
      ...options,
    });
  },

  patch: (endpoint, data, options = {}) => {
    const headers = { ...(options.headers || {}) };
    return apiClient.request(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      headers,
      ...options,
    });
  },

  delete: (endpoint, options = {}) => {
    const headers = { ...(options.headers || {}) };
    return apiClient.request(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
      ...options,
    });
  },

  getBaseUrl: () => API_BASE_URL,
};

